'use client'
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase';

// Banner 数据接口
interface Banner {
  id: string;
  title: string;
  image: string;
  order: number;
  active: boolean;
}

// 为Window添加自定义属性的类型声明
declare global {
  interface Window {
    addRipple: (x: number, y: number, type: 'click' | 'move') => void;
  }
}

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const { t, i18n } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastMousePosition = useRef({ x: 0, y: 0 });
  const lastRippleTime = useRef(0);
  const animationRef = useRef<number>();
  // 追踪鼠标是否移动过的状态
  const [mouseMoved, setMouseMoved] = useState(false);
  // Banner 数据
  const [banners, setBanners] = useState<Banner[]>([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const autoplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 获取 Banner 数据
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const bannersQuery = query(
          collection(db, 'banners'),
          where('active', '==', true),
          orderBy('order', 'asc'),
          limit(5) // 最多获取5个banner
        );

        const snapshot = await getDocs(bannersQuery);
        const bannerData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Banner[];

        setBanners(bannerData);
      } catch (error) {
        console.error('获取Banner错误:', error);
      }
    };

    fetchBanners();
  }, []);

  // Banner 轮播效果
  useEffect(() => {
    if (banners.length <= 1 || autoplayPaused) return;

    // 设置自动轮播定时器
    const interval = setInterval(() => {
      setCurrentBannerIndex(prevIndex =>
        prevIndex === banners.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // 每5秒切换一次

    return () => clearInterval(interval);
  }, [banners, currentBannerIndex, autoplayPaused]); // 添加 autoplayPaused 作为依赖

  // 处理手动切换
  const handleManualChange = useCallback((index: number) => {
    // 设置当前索引
    setCurrentBannerIndex(index);

    // 暂停自动轮播
    setAutoplayPaused(true);

    // 清除之前的超时
    if (autoplayTimeoutRef.current) {
      clearTimeout(autoplayTimeoutRef.current);
    }

    // 设置新的超时，5秒后恢复自动轮播
    autoplayTimeoutRef.current = setTimeout(() => {
      setAutoplayPaused(false);
    }, 5000);
  }, []);

  // 处理上一张/下一张
  const handlePrev = useCallback(() => {
    const newIndex = currentBannerIndex === 0 ? banners.length - 1 : currentBannerIndex - 1;
    handleManualChange(newIndex);
  }, [currentBannerIndex, banners.length, handleManualChange]);

  const handleNext = useCallback(() => {
    const newIndex = currentBannerIndex === banners.length - 1 ? 0 : currentBannerIndex + 1;
    handleManualChange(newIndex);
  }, [currentBannerIndex, banners.length, handleManualChange]);

  // 键盘导航支持
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 只有当轮播有多张图片时才处理键盘事件
      if (banners.length <= 1) return;

      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [banners.length, handlePrev, handleNext]);

  // 设置可见性的效果
  useEffect(() => {
    // 组件挂载后设置为可见
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // 为整个窗口添加鼠标移动监听
  useEffect(() => {
    // 节流函数：限制函数执行频率
    const throttle = (callback: Function, delay: number) => {
      let lastCall = 0;
      return function (...args: any[]) {
        const now = Date.now();
        if (now - lastCall >= delay) {
          lastCall = now;
          callback(...args);
        }
      };
    };

    // 计算两点之间的距离
    const getDistance = (x1: number, y1: number, x2: number, y2: number) => {
      return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    };

    const handleGlobalMouseMove = throttle((e: MouseEvent) => {
      // 获取 Hero 组件相对于视口的位置
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();

      // 检查鼠标是否在 canvas 范围内
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        // 转换为相对于 canvas 的坐标
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // 计算移动距离
        const distance = getDistance(x, y, lastMousePosition.current.x, lastMousePosition.current.y);

        // 只有移动距离超过阈值才更新位置和考虑生成涟漪
        if (distance > 8) {
          setMouseMoved(true);
          lastMousePosition.current = { x, y };

          const now = Date.now();
          // 极低概率生成涟漪，且两次涟漪至少间隔300ms
          if (Math.random() > 0.9 && now - lastRippleTime.current > 300) {
            lastRippleTime.current = now;
            window.addRipple(x, y, 'move');
          }
        }
      }
    }, 20); // 约50fps的刷新率

    // 全局点击处理 - 使用节流
    const handleGlobalClick = throttle((e: MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();

      // 检查点击是否在 canvas 范围内
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // 添加主要涟漪
        window.addRipple(x, y, 'click');
      }
    }, 300); // 点击事件限制更严格，300ms内只能触发一次

    // 添加到全局 window
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('click', handleGlobalClick);

    // 清理函数
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  // 结合实心圆和描边的涟漪效果
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 设置canvas尺寸为窗口大小
    const resizeCanvas = () => {
      const { width, height } = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    // 创建全局涟漪数组
    (window as any).__heroRipples = [];

    // 添加涟漪函数 - 组合实心圆和线条效果
    window.addRipple = (x: number, y: number, type: 'click' | 'move' = 'move') => {
      const ripples = (window as any).__heroRipples || [];

      // 根据类型设置不同的参数
      const maxSize = type === 'click' ? 250 : 170;
      const duration = type === 'click' ? 1800 : 1200; // 持续时间，毫秒
      const baseOpacity = type === 'click' ? 0.2 : 0.15; // 实心圆部分的透明度
      const strokeOpacity = type === 'click' ? 0.3 : 0.22; // 描边部分的透明度

      // 添加新涟漪
      ripples.push({
        x,
        y,
        size: 0,
        strokeSize: 0, // 描边圆的大小，会比填充圆略大
        maxSize,
        opacity: baseOpacity,
        strokeOpacity,
        startTime: performance.now(),
        duration,
        // 为实心部分和描边部分设置不同的颜色
        color: type === 'click' ?
          {
            inner: '255, 255, 255',
            outer: '200, 225, 255',
            stroke: '230, 240, 255'
          } :
          {
            inner: '220, 240, 255',
            outer: '180, 210, 255',
            stroke: '200, 230, 255'
          },
        lineWidth: type === 'click' ? 1.5 : 1,
        // 添加描边延迟效果，让描边圆稍微滞后于实心圆
        strokeDelay: type === 'click' ? 20 : 10 // 毫秒
      });

      // 最多保持8个涟漪，以提高性能
      if (ripples.length > 8) {
        ripples.shift();
      }

      // 存回全局变量
      (window as any).__heroRipples = ripples;
    };

    // 添加初始涟漪
    const addInitialRipples = () => {
      const { width, height } = canvas.getBoundingClientRect();
      for (let i = 0; i < 3; i++) {
        const x = width * (0.3 + Math.random() * 0.4);
        const y = height * (0.3 + Math.random() * 0.4);
        window.addRipple(x, y, Math.random() > 0.5 ? 'click' : 'move');
      }
    };

    // 动画函数 - 结合实心圆和描边效果
    const animate = () => {
      if (!canvas || !ctx) return;

      const { width, height } = canvas.getBoundingClientRect();

      // 清除画布
      ctx.clearRect(0, 0, width, height);

      // 添加淡蓝色背景
      ctx.fillStyle = 'rgba(20, 43, 137, 0.05)';
      ctx.fillRect(0, 0, width, height);

      // 获取全局涟漪数组
      const ripples = (window as any).__heroRipples || [];
      const now = performance.now();

      // 绘制涟漪 - 组合效果
      for (let i = 0; i < ripples.length; i++) {
        const ripple = ripples[i];

        // 计算动画进度 (0-1)
        const progress = (now - ripple.startTime) / ripple.duration;

        // 如果超出持续时间，删除涟漪
        if (progress >= 1) {
          ripples.splice(i, 1);
          i--;
          continue;
        }

        // 使用缓动函数使动画更自然
        const easeProgress = 1 - Math.pow(1 - progress, 3.5);

        // 计算当前大小和透明度
        const currentSize = ripple.maxSize * easeProgress;
        const currentOpacity = ripple.opacity * (1 - easeProgress * 0.85); // 让透明度降低更慢

        // 创建径向渐变
        const gradient = ctx.createRadialGradient(
          ripple.x, ripple.y, 0,
          ripple.x, ripple.y, currentSize
        );

        // 添加渐变色，从中心到边缘，且透明度从高到低
        gradient.addColorStop(0, `rgba(${ripple.color.inner}, ${currentOpacity * 1.2})`);
        gradient.addColorStop(0.65, `rgba(${ripple.color.inner}, ${currentOpacity * 0.8})`);
        gradient.addColorStop(1, `rgba(${ripple.color.outer}, 0)`);

        // 绘制实心圆
        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // 绘制描边效果（可能有多个）
        // 计算描边延迟
        const strokeProgress = Math.max(0, (now - (ripple.startTime + ripple.strokeDelay)) / ripple.duration);

        if (strokeProgress > 0) {
          // 使用不同的缓动曲线，让描边更快速
          const strokeEaseProgress = 1 - Math.pow(1 - strokeProgress, 3);
          const strokeSize = ripple.maxSize * strokeEaseProgress;

          // 描边透明度衰减
          const strokeOpacityFactor = 1 - strokeEaseProgress * 0.7;

          // 绘制主描边
          ctx.beginPath();
          ctx.arc(ripple.x, ripple.y, strokeSize, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${ripple.color.stroke}, ${ripple.strokeOpacity * strokeOpacityFactor})`;
          ctx.lineWidth = ripple.lineWidth;
          ctx.stroke();

          // 添加辅助描边 - 稍微小一点的内圈
          if (ripple.maxSize > 150) {
            ctx.beginPath();
            ctx.arc(ripple.x, ripple.y, strokeSize * 0.85, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(${ripple.color.stroke}, ${ripple.strokeOpacity * strokeOpacityFactor * 0.8})`;
            ctx.lineWidth = ripple.lineWidth * 0.7;
            ctx.stroke();
          }
        }
      }

      // 保存回全局变量
      (window as any).__heroRipples = ripples;

      animationRef.current = requestAnimationFrame(animate);
    };

    // 定时添加随机涟漪，确保即使鼠标不动也有效果
    const intervalId = setInterval(() => {
      const { width, height } = canvas.getBoundingClientRect();
      if (Math.random() > 0.75) { // 略微提高概率
        const x = Math.random() * width;
        const y = Math.random() * height;
        window.addRipple(x, y, Math.random() > 0.7 ? 'click' : 'move');
      }
    }, 2500);

    // 添加初始涟漪
    addInitialRipples();

    // 开始动画
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      clearInterval(intervalId);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // 清除全局变量
      delete (window as any).__heroRipples;
      delete window.addRipple;
    };
  }, []);

  // 简化的 Hero 组件，保留万湖书院内容
  return (
    <div className="relative w-full h-screen overflow-hidden">


      {/* Banner 背景图片 */}
      {banners.length > 0 && (
        <div
          className="absolute inset-0"
          onMouseEnter={() => setAutoplayPaused(true)}
          onMouseLeave={() => {
            // 清除之前的超时
            if (autoplayTimeoutRef.current) {
              clearTimeout(autoplayTimeoutRef.current);
            }
            setAutoplayPaused(false);
          }}
        >
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === currentBannerIndex ? 'opacity-100' : 'opacity-0'}`}
            >
              <img
                src={banner.image}
                alt={banner.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50" />
            </div>
          ))}

          {/* 轮播控制 - 左右箭头 */}
          {banners.length > 1 && (
            <>
              {/* 左箭头 */}
              <button
                onClick={handlePrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition-all duration-300 focus:outline-none"
                aria-label={i18n.language === 'zh' ? "上一张" : "Previous"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>

              {/* 右箭头 */}
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-black/30 hover:bg-black/50 text-white rounded-full p-2 transition-all duration-300 focus:outline-none"
                aria-label={i18n.language === 'zh' ? "下一张" : "Next"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>

              {/* 指示器点 */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleManualChange(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none ${index === currentBannerIndex
                      ? 'bg-white scale-110'
                      : 'bg-white/50 hover:bg-white/70'
                      }`}
                    aria-label={i18n.language === 'zh' ? `切换到第${index + 1}张图片` : `Switch to image ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {/* 简单背景 */}
      <div className="absolute inset-0 bg-black/30">
        <div className="absolute inset-0 bg-gradient-to-b from-[#142b89]/70 via-black/40 to-[#142b89]/90" />
      </div>
      {/* 涟漪效果画布 - 提高 z-index 确保可见 */}
      <div className="absolute inset-0 z-30 overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-none"
          style={{ opacity: 0.95 }}
        />
      </div>
      {/* 内容区域 - 居中显示，增加z-index确保在canvas之上 */}
      <div className="relative z-40 h-full w-full flex flex-col items-center justify-center px-6 text-center pointer-events-auto">
        <div className={`w-full transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '200ms' }}>
          {/* 重新设计的Badge */}
          

          <h1 className="text-[10vw] md:text-[4vw] lg:text-[4vw] font-bold tracking-tight text-white mb-10">
            {i18n.language === 'zh' ? (
              <span className="block" style={{ fontFamily: "'KaiTi', 'STKaiti', serif" }}>
                万湖书院
              </span>
            ) : (
              <span className="block">
                Great Lakes Academy
              </span>
            )}
          </h1>
          <div className="mb-6 flex justify-center">
            <div className="px-4 sm:px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-blue-900/30 via-indigo-900/40 to-blue-900/30 backdrop-blur-lg rounded-xl shadow-xl 
              text-sm sm:text-base md:text-lg font-semibold ring-1 ring-inset ring-white/20 flex flex-wrap justify-center items-center gap-2 sm:gap-3 md:gap-4
              hover:ring-white/30 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 mx-2">
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-blue-100 hover:scale-105 transition-transform duration-300">
                  {i18n.language === 'zh' ? '传承' : 'Inherit'}
                </span>
                <span className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-blue-400/50 animate-pulse"></span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-indigo-100 hover:scale-105 transition-transform duration-300">
                  {i18n.language === 'zh' ? '启蒙' : 'Inspire'}
                </span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                <span className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-indigo-400/50 animate-pulse"></span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-purple-100 hover:scale-105 transition-transform duration-300">
                  {i18n.language === 'zh' ? '创新' : 'Innovate'}
                </span>
                <span className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-purple-400/50 animate-pulse"></span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-200 to-sky-100 hover:scale-105 transition-transform duration-300">
                  {i18n.language === 'zh' ? '引领' : 'Influence'}
                </span>
              </div>
            </div>
          </div>
         

          <div className="mt-12 flex flex-wrap items-center justify-center gap-8">
            <a
              href="#/programs"
              className="rounded-md bg-[#142b89] border border-white/20 px-10 py-5 text-lg font-semibold text-white shadow-sm hover:bg-[#1e3ca8] transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            >
              {t('hero.cta.primary')}
            </a>
            <a
              href="#/about"
              className="group flex items-center text-lg font-semibold text-gray-100 hover:text-blue-200 transition-all duration-300 hover:translate-x-1"
            >
              {t('hero.cta.secondary')}
              <span aria-hidden="true" className="ml-2 transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
